import type { RouterOutputs } from './trpc';

export type Task = RouterOutputs['tasks']['getAll'][number];
export type TaskStatusMeta = RouterOutputs['taskStatuses']['list']['items'][number];
export type WorkspaceMember = { id: number; email: string; fullName: string | null };

export type TimelineFilter = 'all' | 'overdue' | 'today' | 'week' | 'none';
export type GroupByOption = 'none' | 'status' | 'priority' | 'dueDate' | 'assignee';

export interface TaskGroup {
  title: string;
  key: string;
  tasks: Task[];
  colorClass?: string;
  color?: string;
  member?: WorkspaceMember | null;
  overdueCount?: number;
}

/** Fixed scope behind the My Tasks view; an unresolved identity deliberately yields no tasks. */
export function tasksAssignedToUser<T extends { assignedTo: number | null }>(
  tasks: T[],
  currentUserId: number | undefined
): T[] {
  if (currentUserId === undefined) return [];
  return tasks.filter((task) => task.assignedTo === currentUserId);
}

export interface TaskPriorityMeta {
  /** Stored priority value (matches the create/edit dialogs — the only writers). */
  value: number;
  label: 'Low' | 'Medium' | 'High';
  /** Badge/pill classes for this level. */
  className: string;
  /** Text-only class for group headers / minimal contexts. */
  textClass: string;
}

/**
 * The single source of truth for task priority, keyed to what the create/edit
 * dialogs actually store: 0 = Low, 1 = Medium, 2 = High. Highest-first so it can
 * drive ordered UI (filter lists, group headers). Everything that renders or
 * groups priority MUST go through here — do not re-derive a P1/P2/P3 scale, which
 * is what previously drifted out of sync with stored data.
 */
export const TASK_PRIORITY_OPTIONS: readonly TaskPriorityMeta[] = [
  {
    value: 2,
    label: 'High',
    className: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
    textClass: 'text-red-500 dark:text-red-400',
  },
  {
    value: 1,
    label: 'Medium',
    className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    textClass: 'text-amber-500 dark:text-amber-400',
  },
  {
    value: 0,
    label: 'Low',
    className: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
    textClass: 'text-slate-500 dark:text-slate-400',
  },
] as const;

/** Priority meta for a stored value (null/absent → Low). */
export function getPriorityMeta(priority: number | null): TaskPriorityMeta {
  return TASK_PRIORITY_OPTIONS.find((p) => p.value === (priority ?? 0)) ?? TASK_PRIORITY_OPTIONS[2];
}

/**
 * Priority meta only when it's worth surfacing a badge — Medium/High. Low/None
 * return undefined so compact surfaces (cards, list rows) stay uncluttered while
 * still using the same source of truth.
 */
export function getElevatedPriorityMeta(priority: number | null): TaskPriorityMeta | undefined {
  return (priority ?? 0) > 0 ? getPriorityMeta(priority) : undefined;
}

export function getStatusMeta(
  statuses: TaskStatusMeta[],
  statusKey: string | null
): TaskStatusMeta | null {
  return statuses.find((s) => s.key === statusKey) || null;
}

export function getStatusBadgeStyle(statuses: TaskStatusMeta[], statusKey: string | null) {
  const meta = getStatusMeta(statuses, statusKey);
  const color = meta?.color || '#8F86A5';
  return {
    backgroundColor: `${color}1A`,
    color,
    borderColor: `${color}33`,
  };
}

export function isTerminalStatus(statuses: TaskStatusMeta[], statusKey: string | null): boolean {
  return statuses.find((s) => s.key === statusKey)?.isTerminal ?? false;
}

/** To Do is selected when no explicit status tab is present in the URL. */
export function getDefaultTaskStatusTab(statuses: TaskStatusMeta[]): string {
  return (
    statuses.find((status) => status.key === 'todo')?.key ??
    statuses.find((status) => status.isDefault)?.key ??
    statuses.find((status) => !status.isTerminal)?.key ??
    statuses[0]?.key ??
    'todo'
  );
}

/**
 * The status key a completion toggle should write when marking a task done.
 *
 * Callers previously hardcoded `'done'`, which is only correct for a workspace that never
 * renamed or replaced its terminal status. Falls back to the literal only when the workspace
 * has no terminal status configured at all.
 */
export function getTerminalStatusKey(statuses: TaskStatusMeta[]): string {
  return statuses.find((status) => status.isTerminal)?.key ?? 'done';
}

/** The status key a completion toggle should write when re-opening a task. */
export function getOpenStatusKey(statuses: TaskStatusMeta[]): string {
  return getDefaultTaskStatusTab(statuses);
}

/**
 * The status key to write when flipping a task's completion. The one place that decides what
 * "toggle complete" means, so a card, a list row, and the extension all agree.
 */
export function toggleTaskStatusKey(
  statuses: TaskStatusMeta[],
  currentStatus: string | null
): string {
  return isTerminalStatus(statuses, currentStatus)
    ? getOpenStatusKey(statuses)
    : getTerminalStatusKey(statuses);
}

export function orderTaskStatusesForTabs(statuses: TaskStatusMeta[]): TaskStatusMeta[] {
  const defaultStatus = getDefaultTaskStatusTab(statuses);
  return [...statuses].sort(
    (a, b) => Number(b.key === defaultStatus) - Number(a.key === defaultStatus)
  );
}

/** Whether an unfinished task has passed its due timestamp. */
export function isTaskOverdue(
  statuses: TaskStatusMeta[],
  task: { status: string | null; dueAt: number | null },
  nowSec: number = Math.floor(Date.now() / 1000)
): boolean {
  return !!task.dueAt && task.dueAt < nowSec && !isTerminalStatus(statuses, task.status);
}

/** Shared predicate for the status tabs, including the synthetic Overdue tab. */
export function matchesTaskTab(
  statuses: TaskStatusMeta[],
  task: { status: string | null; dueAt: number | null },
  activeTab: string,
  nowSec?: number
): boolean {
  if (activeTab === 'overdue') return isTaskOverdue(statuses, task, nowSec);
  return matchesTaskStatusTab(statuses, task.status, activeTab);
}

/** Shared status-only predicate retained for callers that do not have a whole task. */
export function matchesTaskStatusTab(
  statuses: TaskStatusMeta[],
  taskStatus: string | null,
  activeTab: string
): boolean {
  const status = taskStatus || 'todo';
  if (activeTab === 'all') return !isTerminalStatus(statuses, status);
  return status === activeTab;
}

/**
 * How a list of tasks is bucketed. `panel` is the narrow-column form used beside an entity;
 * the rest are the Tasks page's `?groupBy=` modes.
 */
export type TaskGroupMode = 'none' | 'status' | 'priority' | 'dueDate' | 'assignee' | 'panel';

export interface GroupTasksOptions {
  /** Status-tab context: keeps an empty group visible when its tab is the active one. */
  activeTab?: string;
  /** Injected for testability — no clock mocking needed. */
  nowSec?: number;
  /** Workspace members for assignee grouping. */
  members?: WorkspaceMember[];
}

/**
 * The single definition of how tasks bucket, for every surface.
 *
 * This lived inside a `useMemo` inside `useTaskListFilters`, which reads `useSearchParams` — so a
 * panel with no URL state could not reuse it, and each one wrote its own. Three implementations
 * resulted, two of which disagreed about where an undated task belongs.
 *
 * **An undated task gets its own bucket.** The extension previously filed undated tasks under
 * "Upcoming", alongside tasks genuinely scheduled for later, which hid them behind work that had
 * a date. A task nobody has scheduled is a distinct state and is grouped as one.
 */
export function groupTasksForPanel(
  tasks: Task[],
  statuses: TaskStatusMeta[],
  mode: TaskGroupMode,
  options: GroupTasksOptions = {}
): TaskGroup[] {
  const { activeTab = 'all', nowSec = Math.floor(Date.now() / 1000), members = [] } = options;
  const isTerminal = (statusKey: string | null) => isTerminalStatus(statuses, statusKey);

  if (mode === 'none') {
    return [{ title: 'All Tasks', key: 'all', tasks }];
  }

  if (mode === 'status') {
    return statuses
      .map((status) => ({
        title: status.label,
        key: status.key,
        tasks: tasks.filter((task) => (task.status || 'todo') === status.key),
        color: status.color,
      }))
      .filter((group) => group.tasks.length > 0 || activeTab === 'all' || activeTab === group.key);
  }

  if (mode === 'assignee') {
    const byId = new Map<string, TaskGroup>();
    const order: string[] = [];

    for (const task of tasks) {
      const key = task.assignedTo ? task.assignedTo.toString() : 'unassigned';
      if (!byId.has(key)) {
        const member = task.assignedTo
          ? members.find((m) => m.id === task.assignedTo) || null
          : null;
        byId.set(key, {
          title:
            key === 'unassigned' ? 'Unassigned' : member?.fullName || member?.email || 'Unknown',
          key,
          tasks: [],
          member,
          overdueCount: 0,
        });
        order.push(key);
      }
      const group = byId.get(key)!;
      group.tasks.push(task);
      if (isTaskOverdue(statuses, task, nowSec)) {
        group.overdueCount = (group.overdueCount || 0) + 1;
      }
    }

    return order
      .map((key) => byId.get(key)!)
      .sort((a, b) => {
        if (a.key === 'unassigned') return 1;
        if (b.key === 'unassigned') return -1;
        return a.title.localeCompare(b.title);
      });
  }

  if (mode === 'priority') {
    // Built from the canonical priority scale (High→Low) so group headers can never drift from
    // the badges and filters again.
    return TASK_PRIORITY_OPTIONS.map((meta) => ({
      title: `${meta.label} Priority`,
      key: `priority-${meta.value}`,
      tasks: tasks.filter((task) => (task.priority ?? 0) === meta.value),
      colorClass: meta.textClass,
    })).filter((group) => group.tasks.length > 0);
  }

  const startOfDay = new Date(nowSec * 1000);
  startOfDay.setHours(0, 0, 0, 0);
  const startOfDaySec = Math.floor(startOfDay.getTime() / 1000);
  const endOfDaySec = startOfDaySec + 86400;
  const endOfWeekSec = startOfDaySec + 86400 * 7;

  const overdue = tasks.filter(
    (task) => task.dueAt && task.dueAt < nowSec && !isTerminal(task.status)
  );
  const today = tasks.filter(
    (task) => task.dueAt && task.dueAt >= startOfDaySec && task.dueAt < endOfDaySec
  );
  const noDate = tasks.filter((task) => !task.dueAt);

  if (mode === 'panel') {
    // A panel is a narrow column, so it collapses "this week" and "later" into one Upcoming
    // bucket and surfaces completed work separately at the bottom.
    const done = tasks.filter((task) => isTerminal(task.status));
    const isOpenAndDated = (task: Task) => !!task.dueAt && !isTerminal(task.status);

    return [
      {
        title: 'Overdue',
        key: 'overdue',
        tasks: overdue,
        colorClass: 'text-red-500 dark:text-red-400 font-semibold',
      },
      {
        // Excludes anything already counted as overdue. A task due at 00:01 today is both "due
        // today" and past its time, and a panel renders each bucket in full — so without this it
        // would appear twice. (The Tasks page's `dueDate` mode has the same overlap; it is left
        // as-is here rather than changed as a side effect of this work.)
        title: 'Today',
        key: 'today',
        tasks: today.filter((task) => !isTerminal(task.status) && task.dueAt! >= nowSec),
        colorClass: 'text-blue-500 dark:text-blue-400 font-semibold',
      },
      {
        title: 'Upcoming',
        key: 'upcoming',
        tasks: tasks.filter((task) => isOpenAndDated(task) && task.dueAt! >= endOfDaySec),
      },
      {
        title: 'No Due Date',
        key: 'none',
        tasks: noDate.filter((task) => !isTerminal(task.status)),
        colorClass: 'text-slate-500 dark:text-slate-600',
      },
      {
        title: 'Completed',
        key: 'completed',
        tasks: done,
        colorClass: 'text-muted-foreground',
      },
    ].filter((group) => group.tasks.length > 0);
  }

  return [
    {
      title: 'Overdue',
      key: 'overdue',
      tasks: overdue,
      colorClass: 'text-red-500 dark:text-red-400 font-semibold',
    },
    {
      title: 'Today',
      key: 'today',
      tasks: today,
      colorClass: 'text-blue-500 dark:text-blue-400 font-semibold',
    },
    {
      title: 'This Week',
      key: 'week',
      tasks: tasks.filter(
        (task) => task.dueAt && task.dueAt >= endOfDaySec && task.dueAt < endOfWeekSec
      ),
      colorClass: 'text-yellow-500 dark:text-yellow-400',
    },
    {
      title: 'Upcoming / Future',
      key: 'upcoming',
      tasks: tasks.filter((task) => task.dueAt && task.dueAt >= endOfWeekSec),
      colorClass: 'text-slate-400 dark:text-slate-500',
    },
    {
      title: 'No Due Date',
      key: 'none',
      tasks: noDate,
      colorClass: 'text-slate-500 dark:text-slate-600',
    },
  ].filter((group) => group.tasks.length > 0);
}
